import type { GroupPrice, PackageAddon, PackageTier } from "@/lib/dto";
import { resolveTierPrice } from "@/lib/format";

/**
 * Booking UI works exclusively against these normalized shapes.
 *
 * The API/DTO layer (`PackageTier`, `PackageAddon`) tolerates both snake_case
 * and camelCase and leaves extra keys as `unknown`. Rather than have every
 * consumer re-resolve `default_price`/`defaultPrice`, `cost_include`/
 * `costInclude`, etc. (and get it subtly wrong — see the add-on price bug the
 * wizard shipped with), we resolve the case variants exactly once, here, at the
 * point tiers/add-ons enter the client tree.
 */
export type NormalizedTier = {
  key: string;
  name: string;
  alias?: string;
  abstract?: string;
  displayOrder: number;
  currency?: string;
  defaultPrice: number;
  groupPrices: GroupPrice[];
  costIncludeHtml?: string;
  costExcludeHtml?: string;
};

export type NormalizedAddon = {
  id: number;
  title: string;
  description?: string;
  price: number;
  /** Pricing basis copy, e.g. "Per person" — display-only. */
  unit?: string;
  order: number;
};

const num = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const str = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

/**
 * Resolve a raw tier (snake/camel mixed) into a flat, camelCase tier.
 * `key` is the tier slug the CMS keys the tier map by.
 */
export function normalizeTier(key: string, raw: PackageTier): NormalizedTier {
  const groupPrices = raw.groupPrices ?? raw.group_prices ?? [];
  return {
    key,
    name: str(raw.name) ?? str(raw.alias) ?? key,
    alias: str(raw.alias),
    abstract: str(raw.abstract),
    displayOrder: num(raw.displayOrder) ?? num(raw.display_order) ?? 0,
    currency: str(raw.currency),
    defaultPrice: num(raw.defaultPrice) ?? num(raw.default_price) ?? 0,
    groupPrices,
    costIncludeHtml: str(raw.costInclude) ?? str(raw.cost_include),
    costExcludeHtml: str(raw.costExclude) ?? str(raw.cost_exclude),
  };
}

/** Normalize a tier map into a display-ordered array. */
export function normalizeTiers(
  tiers: Record<string, PackageTier> | null | undefined,
): NormalizedTier[] {
  return Object.entries(tiers ?? {})
    .map(([key, raw]) => normalizeTier(key, raw))
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Resolve a raw add-on into a flat shape.
 *
 * The travel API returns the `addon*`-prefixed form
 * (`addonTitle`/`addonPrice`/`addonDescription`/`addonUnit`); some endpoints
 * return the bare `title`/`price` form instead. Reading only one of the two
 * silently produced `price: 0` and blank titles, so both are resolved here —
 * prefixed keys first, since that is what the package-detail payload sends.
 */
export function normalizeAddon(raw: PackageAddon): NormalizedAddon {
  return {
    id: num(raw.id) ?? 0,
    title: str(raw.addonTitle) ?? str(raw.title) ?? str(raw.name) ?? "Add-on",
    description: str(raw.addonDescription) ?? str(raw.description),
    price: num(raw.addonPrice) ?? num(raw.price) ?? 0,
    unit: str(raw.addonUnit),
    order: num(raw.addonOrder) ?? 0,
  };
}

/** Normalize add-ons into display order (`addonOrder`, stable within ties). */
export function normalizeAddons(
  addons: PackageAddon[] | null | undefined,
): NormalizedAddon[] {
  return (addons ?? [])
    .map(normalizeAddon)
    .sort((a, b) => a.order - b.order);
}

/**
 * The per-person price for `travelers` people: the matching group-price
 * bracket if one covers that party size, else the tier's flat price.
 * Single source of truth for "price for N travelers" across the wizard,
 * sidebar, and mobile bar.
 */
export function resolvePerPersonPrice(
  source: { defaultPrice?: number | null; groupPrices?: GroupPrice[] },
  travelers: number,
): number {
  const bracket = (source.groupPrices ?? []).find(
    (gp) => travelers >= gp.minPerson && travelers <= gp.maxPerson,
  );
  if (bracket && bracket.price > 0) return bracket.price;
  return resolveTierPrice({
    defaultPrice: source.defaultPrice,
    groupPrices: source.groupPrices,
  });
}
